import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { ProductService } from "src/app/shared/services/products/products/product.service";
import { AppState } from "../../reducers";

import * as ProductFamilyActionTypes from "../../actions/productFamilies/productFamilies.actions";
import * as MessagesActionTypes from "../../actions/messages/message.actions";
import { from } from "rxjs";
import { exhaustMap, map, catchError, tap, mergeMap } from "rxjs/operators";
import { ProductFamilyService } from "src/app/shared/services/products/productFamily/productFamily.service";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { SnackBarUtils } from "src/app/shared/utils/snackbarUtils";

@Injectable()
export class ProductFamilyEffects {
  constructor(
    private actions$: Actions,
    private productFamilyService: ProductFamilyService,
    private translate: TranslateService,
    private store$: Store<AppState>
  ) {}

  getProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductFamilyActionTypes.getProductFamiliesList),
      exhaustMap((action) =>
        this.productFamilyService
          .getProductFamilies(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              ProductFamilyActionTypes.loadProductFamiliesList({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                ProductFamilyActionTypes.loadProductFamiliesList({
                  response: [],
                }),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("ProductFamily.loadError"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getExcelProductFamily$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductFamilyActionTypes.getProductFamiliesExcelList),
      exhaustMap((action) =>
        this.productFamilyService
          .getProductFamilies(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              ProductFamilyActionTypes.loadProductFamiliesExcelList({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                ProductFamilyActionTypes.loadProductFamiliesExcelList({
                  response: [],
                }),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("ProductFamily.loadProductFamily"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );
  getProductFamily$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductFamilyActionTypes.getProductFamily),
      exhaustMap((action) =>
        this.productFamilyService.getProductFamily(action.code).pipe(
          map((result) =>
            ProductFamilyActionTypes.loadProductFamily({ response: result })
          ),
          catchError((error) => {
            const actions = [
              ProductFamilyActionTypes.loadProductFamily({ response: [] }),
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("Products.loadProductError"),
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );
  createProductFamily$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductFamilyActionTypes.createProductFamily),
      exhaustMap((action) =>
        this.productFamilyService
          .createProductFamily(action.productFamily)
          .pipe(
            mergeMap((result) => [
              ProductFamilyActionTypes.resetCreation(),
              ProductFamilyActionTypes.getProductFamily({
                code: result?.result?.code,
              })
            ]),
            tap(() =>
              this.store$.dispatch(
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("ProductFamily.CreateProductFamily"),
                    SnackbarType.SUCCESS
                  )
                )
              )
            ),
            catchError((error) => {
              const actions = [
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant(
                      "ProductFamily.errorCreateProductFamily"
                    ),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );
  updateProductFamily$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductFamilyActionTypes.updateProductFamily),
      exhaustMap((action) =>
        this.productFamilyService
          .updateProductFamily(action.productFamily)
          .pipe(
            map((result) => {
              return ProductFamilyActionTypes.getProductFamily({
                code: result?.result?.code,
              });
            }),
            tap(() =>
              this.store$.dispatch(
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant(
                      "ProductFamily.UpdateProductFamily"
                    ),
                    SnackbarType.SUCCESS
                  )
                )
              )
            ),
            catchError((error) => {
              const actions = [
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant(
                      "ProductFamily.errorUpdateProductFamily"
                    ),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );
}
